import { useRuntimeConfig } from '#imports'

import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'

import type { GetOfferV1Response } from '../../api/types'

const DEFAULT_OFFER_TTL_MS = 3 * 24 * 60 * 60 * 1000 // 3 days

type PersistedOffer = {
  offer: GetOfferV1Response
  timestamp: number
}

/**
 * Local storage persistence of the buyback offer.
 *
 * Used on markets without cart storage (e.g. APAC trade-in markets): the
 * accepted offer is persisted locally and restored on mount by
 * 'useBuybackOffer'.
 */
export function useOfferLocalStorage() {
  const localStorage = useLocalStorage()
  const marketKey = useMarketplace().market.countryCode.toLowerCase()
  const {
    public: { TRADEIN_AU_OFFER_TTL_MS },
  } = useRuntimeConfig()

  const STORAGE_KEY = `tradein_${marketKey}_accepted_offer`
  const offerTtlMs = Number(TRADEIN_AU_OFFER_TTL_MS) || DEFAULT_OFFER_TTL_MS

  function writeOfferToLocalStorage(offer: GetOfferV1Response) {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ offer, timestamp: Date.now() }),
    )
  }

  function readOfferFromLocalStorage() {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return undefined

    try {
      const persisted = JSON.parse(stored) as PersistedOffer

      if (Date.now() - persisted.timestamp > offerTtlMs) {
        removeOfferFromLocalStorage()

        return undefined
      }

      return persisted.offer
    } catch {
      // corrupted data in store - not supposed to happen
      removeOfferFromLocalStorage()

      return undefined
    }
  }

  function removeOfferFromLocalStorage() {
    localStorage.removeItem(STORAGE_KEY)
  }

  return {
    writeOfferToLocalStorage,
    readOfferFromLocalStorage,
    removeOfferFromLocalStorage,
  }
}
